/**
 * Générateur — Automatisme : reconnaître des vues (de dessus, dessous…)
 * d'empilements de cubes.
 * Attendu ID : 1066 | Niveau : 5ème | Domaine : Représentation de l'espace
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - La bonne vue            (QCM planche ①-④ ; V/F sur quatre vues numérotées)
 *   - Vue codée               (QCM planche de colonnes ; saisie)
 *   - Vues : vrai ou faux ?   (V/F de pur rappel)
 *
 * ── Plan de cartes validé (2026-09-04) ─────────────────────────────────────
 * Source : Automatisme n°28 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * P1 : INTRODUIT au CM1, CONSOLIDÉ au CM2 (problèmes sur des assemblages de
 * cubes, perspective cavalière comme registre), ÉTENDU en 6e (passage 3D ↔ 2D
 * dans les deux sens), automatisme en 5e. P2 : sens installés — assembler,
 * décrire, dénombrer ; sens de 6e — la projection (face, dessus, côté). Non
 * installés : la vue codée, la vue de dessous en miroir, les cubes cachés.
 * Distracteurs : une rangée recopiée au lieu du plus grand nombre, la vue en
 * miroir, la somme d'une colonne, le nombre de piles. P3 : base 3 × 2 ou
 * 3 × 3, hauteurs 0 à 3, au plus 12 cubes. P5 : perspective isométrique avec
 * trièdre (avant, droite, haut), quadrillages 2D, tableau codé ; pas de
 * repère. P6 : aucun instrument, pas de calculatrice. P7 : pas de
 * dénombrement du total (automatisme voisin), pas de patron, pas de solide
 * autre que des cubes. P8 : vue de face, de dessus, de dessous, de gauche, de
 * droite ; empilement, pile, colonne, rangée ; vue codée. P10 : constat sur
 * la figure (« laquelle », « combien »). P11 : la vue de dessus ne dit pas les
 * hauteurs ; deux empilements pour une même vue de dessus ; cubes cachés ;
 * dessous/dessus et gauche/droite en miroir. P12 : rémanence forte.
 *
 * ── Conventions géométriques ───────────────────────────────────────────────
 * h[r][c] : hauteur de la pile en rangée r (r = 0 : rangée de DEVANT) et
 * colonne c (c = 0 : à GAUCHE vu de face). Perspective : l'observateur est
 * devant à droite ; la face visible en bas à gauche est la FACE AVANT, celle
 * en bas à droite est le côté DROIT.
 *   - vue de face   : colonnes c de gauche à droite, hauteur = max sur r ;
 *   - vue de dessus : rangée de devant EN BAS, colonnes c de gauche à droite ;
 *   - vue de dessous : vue de dessus retournée gauche-droite (miroir) ;
 *   - vue de droite : convention PHYSIQUE (décision prof 2026-09-04) :
 *     l'observateur à droite voit la rangée de devant à sa GAUCHE (colonnes
 *     r = 0 … R−1 de gauche à droite), hauteur = max sur c ;
 *   - vue de gauche : miroir de la vue de droite.
 * INVARIANTS :
 *   - toutes les vues sont CALCULÉES depuis h ; les candidats d'une planche
 *     sont deux à deux distincts ; une seule bonne réponse ;
 *   - data-hauteurs, data-grille, data-colonnes dans les SVG pour les solveurs ;
 *   - V/F : jamais de crochets, mixité 1-3 faux, fenêtre stricte ;
 *   - réponses ENTIÈRES ; pas de calculatrice.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';
import { svgPerspective, aucunCubeCache, cleH } from './svgCubes';

const ATTENDU_ID = 1066;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_VUE = 'La bonne vue';
const L_CODEE = 'Vue codée';
const L_VF = 'Vues : vrai ou faux ?';

const BLEU = '#1E3A8A';
const NUMS = ['①', '②', '③', '④'];
const NOMS_VUES = { face: 'de face', dessus: 'de dessus', dessous: 'de dessous', droite: 'de droite', gauche: 'de gauche' };

// ─── Empilements et vues ───────────────────────────────────────────────────

const cleG = (g) => `${g.length}x${g[0].length}:${g.map((row) => row.join('')).join('/')}`;
const egales = (g1, g2) => cleG(g1) === cleG(g2);
const miroir = (g) => g.map((row) => [...row].reverse());
const maxCol = (h, c) => Math.max(...h.map((row) => row[c]));
const maxRow = (row) => Math.max(...row);

/** Grille 0/1 d'une vue de face à partir d'une liste de hauteurs (colonnes de gauche à droite). */
const grilleColonnes = (hauteurs) => {
    const H = Math.max(...hauteurs);
    return Array.from({ length: H }, (_, i) => hauteurs.map((hh) => (hh >= H - i ? 1 : 0)));
};

const hauteursFace = (h) => h[0].map((_, c) => maxCol(h, c));
// Convention PHYSIQUE (décision prof 2026-09-04, après hésitation avec le corrigé de la
// fiche qui lit les rangées du fond vers l'avant) : l'observateur placé à droite voit la
// rangée de devant à sa GAUCHE (colonnes r = 0 … R−1). La vue de gauche en est le miroir.
const hauteursDroite = (h) => h.map(maxRow);

const vue = (h, nom) => {
    if (nom === 'face') return grilleColonnes(hauteursFace(h));
    if (nom === 'droite') return grilleColonnes(hauteursDroite(h));
    if (nom === 'gauche') return miroir(grilleColonnes(hauteursDroite(h)));
    // dessus : rangée de devant en bas → lignes r = R−1 … 0
    const dessus = [...h].reverse().map((row) => row.map((v) => (v > 0 ? 1 : 0)));
    if (nom === 'dessus') return dessus;
    return miroir(dessus);                                   // dessous
};

const TOUTES_VUES = ['face', 'dessus', 'dessous', 'droite', 'gauche'];


/**
 * Empilement aléatoire : R rangées (2 ou 3) × 3 colonnes, hauteurs 0-3,
 * 4 à 12 cubes, aucune colonne ni rangée entièrement vide.
 * `distinctes` : noms de vues qui doivent être deux à deux différentes.
 */
const tirerEmpilement = (distinctes = TOUTES_VUES) => {
    // Le nombre de rangées est tiré D'ABORD (2 ou 3 à parts égales, revue 2026-09-04, A8 :
    // les gardes rejetaient surtout les tirages à 3 rangées) ; seules les hauteurs sont retirées.
    const R = randomChoice([2, 3]); const C = 3;
    for (let essai = 0; essai < 2000; essai++) {
        const h = Array.from({ length: R }, () => Array.from({ length: C }, () => randInt(0, 3)));
        const total = h.flat().reduce((a, b) => a + b, 0);
        if (total < 4 || total > 12) continue;
        if (h[0].some((_, c) => maxCol(h, c) === 0) || h.some((row) => maxRow(row) === 0)) continue;
        if (!aucunCubeCache(h)) continue;
        const grilles = distinctes.map((n) => vue(h, n));
        let ok = true;
        for (let i = 0; i < grilles.length && ok; i++) for (let j = i + 1; j < grilles.length; j++) if (egales(grilles[i], grilles[j])) { ok = false; break; }
        if (!ok) continue;
        return h;
    }
    throw new Error('gen_1066 : empilement introuvable');
};

// ─── Rendu : perspective isométrique → moteur commun svgCubes.js ─────────

// ─── Rendu : grilles et planches ───────────────────────────────────────────

const CELL = 16;

/** Une grille 0/1, cases pleines colorées, à l'offset (ox, oy). */
const dessinerGrille = (g, ox, oy, couleur = '#bfdbfe') => {
    let s = '';
    g.forEach((row, i) => row.forEach((v, j) => {
        if (v) s += `<rect x="${ox + j * CELL}" y="${oy + i * CELL}" width="${CELL}" height="${CELL}" fill="${couleur}" stroke="#1e293b" stroke-width="1.2"/>`;
    }));
    return s;
};

/** Planche 2 × 2 de grilles numérotées, centrées dans des panneaux de 100 × 92. */
const svgPlancheGrilles = (grilles, extra = '') => {
    const PW = 100; const PH = 92;
    let s = '';
    grilles.forEach((g, i) => {
        const ox = (i % 2) * PW; const oy = Math.floor(i / 2) * PH;
        const gw = g[0].length * CELL; const gh = g.length * CELL;
        s += `<g data-index="${i + 1}" data-grille="${cleG(g)}">`
            + `<rect x="${ox + 1}" y="${oy + 1}" width="${PW - 2}" height="${PH - 2}" fill="#fff" stroke="#e2e8f0"/>`
            + `<text x="${ox + 8}" y="${oy + 17}" font-size="14" font-weight="800" fill="${BLEU}">${NUMS[i]}</text>`
            + dessinerGrille(g, ox + Math.round((PW - gw) / 2) + 8, oy + 14 + Math.round((PH - 14 - gh) / 2))
            + '</g>';
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${2 * PW} ${2 * PH}" width="${2 * PW}" height="${2 * PH}" font-family="Arial"${extra}>${s}</svg>`;
};

/** Perspective + planche côte à côte dans un seul SVG (la planche à droite). */
const svgPerspectiveEtPlanche = (h, grilles, extra = '') => {
    const persp = svgPerspective(h); const planche = svgPlancheGrilles(grilles);
    const [, pw, ph] = persp.match(/viewBox="0 0 (\d+) (\d+)"/).map(Number);
    const [, gw, gh] = planche.match(/viewBox="0 0 (\d+) (\d+)"/).map(Number);
    const H = Math.max(ph, gh); const gap = 16;
    const corps = (svg) => svg.replace(/^<svg[^>]*>/, '').replace(/<\/svg>$/, '');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${pw + gap + gw} ${H}" width="${pw + gap + gw}" height="${H}" font-family="Arial" data-hauteurs="${cleH(h)}"${extra}>`
        + `<g transform="translate(0,${Math.round((H - ph) / 2)})">${corps(persp)}</g>`
        + `<g transform="translate(${pw + gap},${Math.round((H - gh) / 2)})">${corps(planche)}</g></svg>`;
};

/** Tableau codé : vue de dessus avec le nombre de cubes par case ; « face » en bas, « droite » à droite. */
const svgTableauCode = (h, extra = '') => {
    const R = h.length; const C = h[0].length; const cell = 26; const ox = 12; const oy = 10;
    let s = '';
    // lignes du tableau : rangée de devant EN BAS
    for (let i = 0; i < R; i++) for (let j = 0; j < C; j++) {
        const r = R - 1 - i; const v = h[r][j];
        // case vide : 0 aussi net que les autres nombres, fond gris (retour prof 2026-09-04)
        s += `<rect x="${ox + j * cell}" y="${oy + i * cell}" width="${cell}" height="${cell}" fill="${v ? '#eff6ff' : '#e2e8f0'}" stroke="#1e293b" stroke-width="1.2"/>`
            + `<text x="${ox + j * cell + cell / 2}" y="${oy + i * cell + cell / 2 + 5}" font-size="14" font-weight="700" fill="${v ? BLEU : '#334155'}" text-anchor="middle">${v}</text>`;
    }
    const tw = C * cell; const th = R * cell;
    // repères d'observation
    s += `<text x="${ox + tw / 2}" y="${oy + th + 22}" font-size="10.5" font-weight="700" fill="#7c3aed" text-anchor="middle">↑ vue de face</text>`;
    s += `<text x="${ox + tw + 10}" y="${oy + th / 2 + 4}" font-size="10.5" font-weight="700" fill="#dc2626" text-anchor="start">← vue de droite</text>`;
    const w = ox + tw + 100; const hh = oy + th + 30;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${hh}" width="${w}" height="${hh}" font-family="Arial" data-hauteurs="${cleH(h)}"${extra}>${s}</svg>`;
};

/** Planche 2 × 2 de dessins en colonnes de carreaux (listes de hauteurs). */
const svgPlancheColonnes = (listes, extra = '') => {
    const PW = 104; const PH = 84; const cell = 14;
    let s = '';
    listes.forEach((hs, i) => {
        const ox = (i % 2) * PW; const oy = Math.floor(i / 2) * PH;
        const gw = hs.length * cell; const H = Math.max(...hs); const gh = H * cell;
        const x0 = ox + Math.round((PW - gw) / 2) + 8; const y0 = oy + PH - 8;
        s += `<g data-index="${i + 1}" data-colonnes="${hs.join(',')}">`
            + `<rect x="${ox + 1}" y="${oy + 1}" width="${PW - 2}" height="${PH - 2}" fill="#fff" stroke="#e2e8f0"/>`
            + `<text x="${ox + 8}" y="${oy + 17}" font-size="14" font-weight="800" fill="${BLEU}">${NUMS[i]}</text>`;
        hs.forEach((hh, j) => { for (let k = 0; k < hh; k++) s += `<rect x="${x0 + j * cell}" y="${y0 - (k + 1) * cell}" width="${cell}" height="${cell}" fill="#bfdbfe" stroke="#1e293b" stroke-width="1.2"/>`; });
        s += `<line x1="${x0 - 4}" y1="${y0}" x2="${x0 + gw + 4}" y2="${y0}" stroke="#475569" stroke-width="1.2"/>`;
        void gh;
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${2 * PW} ${2 * PH}" width="${2 * PW}" height="${2 * PH}" font-family="Arial"${extra}>${s}</svg>`;
};

// ─── Carte 1a : la bonne vue (QCM planche) ─────────────────────────────────

let derniereSignatureVue1066 = null;

const genVueQcm = () => {
    let payload = construireVueQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureVue1066; t++) payload = construireVueQcm();
    derniereSignatureVue1066 = payload._meta.params.signature;
    return payload;
};

const construireVueQcm = () => {
    // La vue demandée est tirée D'ABORD (cinq vues à parts égales), l'empilement ensuite.
    const cible = randomChoice(TOUTES_VUES);
    for (let essai = 0; essai < 60; essai++) {
        const h = tirerEmpilement();
        const bonne = vue(h, cible);
        // leurres : les autres vues et le miroir de la bonne, deux à deux distincts et distincts de la bonne
        const pool = shuffle([...TOUTES_VUES.filter((n) => n !== cible).map((n) => ({ g: vue(h, n), role: n })), { g: miroir(bonne), role: 'miroir' }]);
        const leurres = [];
        pool.forEach((cand) => {
            if (leurres.length >= 3) return;
            if (egales(cand.g, bonne) || leurres.some((l) => egales(l.g, cand.g))) return;
            leurres.push(cand);
        });
        if (leurres.length < 3) continue;
        const panneaux = shuffle([{ g: bonne, role: cible }, ...leurres]);
        const bonneIndex = panneaux.findIndex((p) => p.role === cible);
        const options = NUMS.map((n) => `Vue ${n}`);
        const svg = svgPerspectiveEtPlanche(h, panneaux.map((p) => p.g), ` data-roles="${panneaux.map((p) => p.role).join('|')}"`);
        const texte = `<p>L'empilement de cubes est dessiné en perspective ; les flèches indiquent l'avant, la droite et le haut. Parmi les quatre vues proposées, une seule est la vue <strong>${NOMS_VUES[cible]}</strong> de cet empilement.</p><p>Laquelle ?</p>`;
        const payload = makeQcm(options, options[bonneIndex], texte, { ...opts(L_VUE), elements_additionnels: imgElement(svg, 'large') });
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement', sousType: cible };
        payload._meta.params = { cible, h, bonneIndex, roles: panneaux.map((p) => p.role), signature: `${cible}-${cleH(h)}-${bonneIndex}` };
        return payload;
    }
    throw new Error('gen_1066 : planche de vues introuvable');
};

// ─── Carte 1b : la bonne vue (V/F sur quatre vues numérotées) ──────────────

let dernieresLignesVueVf1066 = [];

const genVueVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const h = tirerEmpilement();
        // quatre vues parmi les cinq, chacune n'est la vue que d'UN seul côté (vues deux à deux distinctes)
        const noms = shuffle([...TOUTES_VUES]).slice(0, 4);
        const panneaux = noms.map((n) => ({ g: vue(h, n), nom: n }));
        // nombre de faux voulu, puis une ligne par panneau : le vrai nom ou un autre nom
        const nbFaux = randInt(1, 3);
        const fauxIdx = new Set(shuffle([0, 1, 2, 3]).slice(0, nbFaux));
        // Un faux nom désigne de préférence une vue de MÊMES DIMENSIONS que la grille (le miroir
        // d'abord, puis face/côté de même taille) : une prétention rejetable sur la seule forme
        // n'exerce rien (revue 2026-09-04, A3). Un nom quelconque n'est pris qu'à défaut.
        const MIROIRS = { droite: 'gauche', gauche: 'droite', dessus: 'dessous', dessous: 'dessus' };
        const dims = (g) => `${g.length}x${g[0].length}`;
        const fauxNom = (nom, g) => {
            const autres = TOUTES_VUES.filter((n) => n !== nom);
            const memeTaille = autres.filter((n) => dims(vue(h, n)) === dims(g));
            if (MIROIRS[nom] && memeTaille.includes(MIROIRS[nom]) && Math.random() < 0.6) return MIROIRS[nom];
            return randomChoice(memeTaille.length ? memeTaille : autres);
        };
        const lignes = panneaux.map((p, i) => {
            const pretendu = fauxIdx.has(i) ? fauxNom(p.nom, p.g) : p.nom;
            return { l: `La vue ${NUMS[i]} est la vue ${NOMS_VUES[pretendu]}.`, v: pretendu === p.nom, pretendu };
        });
        if (essai > 120) dernieresLignesVueVf1066 = [];
        if (lignes.some((it) => dernieresLignesVueVf1066.includes(it.l))) continue;
        dernieresLignesVueVf1066 = lignes.map((it) => it.l);
        const svg = svgPerspectiveEtPlanche(h, panneaux.map((p) => p.g), ` data-roles="${panneaux.map((p) => p.nom).join('|')}"`);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>L\'empilement de cubes est dessiné en perspective ; les flèches indiquent l\'avant, la droite et le haut. Les quatre quadrillages sont des vues de cet empilement. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            { ...opts(L_VUE), elements_additionnels: imgElement(svg, 'large') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { h, noms, pretendus: lignes.map((it) => it.pretendu) };
        return payload;
    }
    throw new Error('gen_1066 : V/F vues sans tirage après 160 essais');
};

// ─── Carte 2a : vue codée (QCM planche de colonnes) ────────────────────────

const sommeCol = (h, c) => h.reduce((a, row) => a + row[c], 0);
const nbNonNulsCol = (h, c) => h.reduce((a, row) => a + (row[c] > 0 ? 1 : 0), 0);
const memeListe = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

let derniereSignatureCodee1066 = null;

const genCodeeQcm = () => {
    let payload = construireCodeeQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureCodee1066; t++) payload = construireCodeeQcm();
    derniereSignatureCodee1066 = payload._meta.params.signature;
    return payload;
};

const construireCodeeQcm = () => {
    const cible = randomChoice(['face', 'droite']);
    for (let essai = 0; essai < 80; essai++) {
        const h = tirerEmpilement(['face', 'droite']);
        const R = h.length; const C = h[0].length;
        let bonne; let pool;
        if (cible === 'face') {
            bonne = hauteursFace(h);
            pool = [
                { hs: h[0], role: 'rangee_devant' },                                   // rangée de devant recopiée
                { hs: h[R - 1], role: 'rangee_fond' },                                 // rangée du fond recopiée
                { hs: h[0].map((_, c) => sommeCol(h, c)), role: 'somme' },              // somme de la colonne
                { hs: h[0].map((_, c) => nbNonNulsCol(h, c)), role: 'nb_piles' },        // nombre de piles de la colonne
                { hs: hauteursDroite(h), role: 'droite' },                             // l'autre vue (nombre de colonnes différent)
            ];
        } else {
            bonne = hauteursDroite(h);
            pool = [
                { hs: h.map((row) => row[0]), role: 'colonne_gauche' },
                { hs: h.map((row) => row[C - 1]), role: 'colonne_droite' },
                { hs: h.map((row) => row.reduce((a, b) => a + b, 0)), role: 'somme' },
                { hs: h.map((row) => row.filter((v) => v > 0).length), role: 'nb_piles' },
                { hs: hauteursFace(h), role: 'face' },
            ];
        }
        // Les 0 sont AUTORISÉS dans les leurres (colonne vide dessinée comme un trou, cf. le dessin
        // « 1, 3 et 0 » de la fiche) et une rangée / colonne RECOPIÉE est servie en priorité quand elle
        // diffère de la bonne réponse : c'est le leurre principal de la fiche (revue 2026-09-04, A3).
        // Hauteur ≤ 4 : au-delà, la colonne touche le numéro du panneau.
        const admissible = (cand, deja) => !memeListe(cand.hs, bonne) && !deja.some((l) => memeListe(l.hs, cand.hs))
            && Math.max(...cand.hs) <= 4 && cand.hs.some((v) => v > 0);
        const recopies = shuffle(pool.filter((c) => /^(rangee|colonne)_/.test(c.role)));
        const leurres = [];
        recopies.forEach((cand) => { if (leurres.length < 1 && admissible(cand, leurres)) leurres.push(cand); });
        shuffle(pool.filter((c) => !recopies.includes(c))).forEach((cand) => { if (leurres.length < 3 && admissible(cand, leurres)) leurres.push(cand); });
        recopies.forEach((cand) => { if (leurres.length < 3 && admissible(cand, leurres)) leurres.push(cand); });
        if (leurres.length < 3) continue;
        const panneaux = shuffle([{ hs: bonne, role: cible }, ...leurres]);
        const bonneIndex = panneaux.findIndex((p) => p.role === cible);
        const options = NUMS.map((n) => `Dessin ${n}`);
        const svg = svgTableauCode(h); const planche = svgPlancheColonnes(panneaux.map((p) => p.hs), ` data-roles="${panneaux.map((p) => p.role).join('|')}"`);
        const question = cible === 'face' ? 'la vue de face (on regarde depuis le bas du tableau)' : 'la vue de droite (on regarde depuis la droite du tableau)';
        const texte = `<p>Un empilement de cubes est décrit par sa vue de dessus <strong>codée</strong> : dans chaque case est écrit le nombre de cubes empilés. Quatre dessins faits de colonnes de carreaux sont proposés. Un seul est <strong>${question}</strong>.</p><p>Lequel ?</p>`;
        const payload = makeQcm(options, options[bonneIndex], texte, {
            ...opts(L_CODEE), elements_additionnels: { version: 1, elements: [...imgElement(svg, 'medium').elements, ...imgElement(planche, 'medium').elements] },
        });
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement', sousType: cible };
        payload._meta.params = { cible, h, bonneIndex, roles: panneaux.map((p) => p.role), signature: `${cible}-${cleH(h)}-${bonneIndex}` };
        return payload;
    }
    throw new Error('gen_1066 : planche codée introuvable');
};

// ─── Carte 2b : vue codée (saisie) ─────────────────────────────────────────

let derniereSignatureSaisie1066 = null;

const genCodeeSaisie = () => {
    let payload = construireCodeeSaisie();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureSaisie1066; t++) payload = construireCodeeSaisie();
    derniereSignatureSaisie1066 = payload._meta.params.signature;
    return payload;
};

const ORDINAUX = ['1re', '2e', '3e'];

const construireCodeeSaisie = () => {
    const sousType = randomChoice(['carreaux', 'face_colonne', 'hauteur_max']);
    let h; let reponse; let question; let detail; let voulu = null;
    for (let essai = 0; essai < 200; essai++) {
        h = tirerEmpilement(['face', 'droite']);
        if (sousType === 'carreaux') {
            // au moins une case vide, sinon la réponse est le nombre de cases
            if (!h.flat().some((v) => v === 0)) continue;
            reponse = h.flat().filter((v) => v > 0).length;
            question = 'Combien de carreaux compte la vue de dessus (sans les nombres) de cet empilement ?'; detail = '';
        } else if (sousType === 'face_colonne') {
            // la colonne demandée est tirée d'abord (revue 2026-09-04, A8 : la garde rejetait surtout le milieu)
            if (voulu === null) voulu = randInt(0, h[0].length - 1);
            const c = voulu;
            // la colonne doit contenir deux hauteurs différentes non nulles : le plus grand nombre n'est pas une simple lecture
            const vals = h.map((row) => row[c]).filter((v) => v > 0);
            if (new Set(vals).size < 2) continue;
            reponse = maxCol(h, c);
            question = `Sur la vue de face (on regarde depuis le bas du tableau), quelle est la hauteur, en carreaux, de la ${ORDINAUX[c]} colonne en partant de la gauche ?`; detail = String(c);
        } else {
            // la hauteur maximale VOULUE est tirée d'abord (2 ou 3 à parts égales, revue 2026-09-04, A2 :
            // avec des hauteurs de 0 à 3 sur 6 à 9 cases, la réponse était 3 dans 74 % des tirages)
            if (voulu === null) voulu = randomChoice([2, 3]);
            if (Math.max(...h.flat()) !== voulu) continue;
            reponse = voulu;
            question = 'Sur la vue de face, quelle est la hauteur, en carreaux, de la colonne la plus haute ?'; detail = '';
        }
        break;
    }
    if (!h || reponse === undefined) throw new Error('gen_1066 : saisie codée introuvable');
    const svg = svgTableauCode(h);
    const payload = makeSaisie(String(reponse), `<p>Un empilement de cubes est décrit par sa vue de dessus <strong>codée</strong> : dans chaque case est écrit le nombre de cubes empilés.</p><p>${question}</p>`, {
        ...opts(L_CODEE), suffixe: 'carreaux', elements_additionnels: imgElement(svg, 'medium'),
    });
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, h, reponse, signature: `${sousType}-${cleH(h)}-${detail}` };
    return payload;
};

// ─── Carte 3 : vrai ou faux ? ──────────────────────────────────────────────

const POOL_VF = [
    { l: 'La vue de dessus indique l\'emplacement des piles de cubes, pas leur hauteur.', v: true },
    { l: 'Deux empilements différents peuvent avoir la même vue de dessus.', v: true },
    { l: 'La vue de dessous a la même forme que la vue de dessus, retournée comme dans un miroir.', v: true },
    { l: 'Sur la vue de face, la hauteur d\'une colonne est celle de la pile la plus haute de cette colonne.', v: true },
    { l: 'Sur une vue de face, un cube caché derrière un autre n\'apparaît pas.', v: true },
    { l: 'Le nombre de carreaux de la vue de dessus est le nombre de piles de cubes posées au sol.', v: true },
    { l: 'La vue de dessus permet de connaître la hauteur de chaque pile de cubes.', v: false },
    { l: 'La vue de face permet de connaître le nombre total de cubes de l\'empilement.', v: false },
    { l: 'Deux empilements qui ont la même vue de dessus sont forcément identiques.', v: false },
    { l: 'La vue de dessous est toujours identique à la vue de dessus.', v: false },
    { l: 'La vue de gauche et la vue de droite d\'un empilement sont toujours identiques.', v: false },
    { l: 'Sur la vue de face, la hauteur d\'une colonne est la somme des hauteurs des piles de cette colonne.', v: false },
];
if (POOL_VF.filter((it) => it.v).length !== 6) throw new Error('gen_1066 : pool V/F déséquilibré');
POOL_VF.forEach((it) => { if (/[[\]]/.test(it.l)) throw new Error('gen_1066 : crochets dans une ligne V/F'); });

// Familles : deux lignes d'une même famille se répondent mot pour mot (vrai / faux sur le même
// fait) et ne sont jamais servies ensemble (revue 2026-09-04, A8 : 49 % des tableaux).
const FAMILLE_VF = (l) => {
    if (/même vue de dessus/.test(l)) return 'meme_dessus';
    if (/vue de dessous/.test(l)) return 'dessous';
    if (/hauteur d'une colonne/.test(l)) return 'hauteur_colonne';
    if (/(hauteur de chaque pile|emplacement des piles)/.test(l)) return 'dessus_hauteur';
    if (/nombre total de cubes|cube caché/.test(l)) return 'total';
    return l;
};

let derniersServisVf1066 = [];

const genVf = () => {
    for (let essai = 0; essai < 120; essai++) {
        const dispo = POOL_VF.filter((it) => !derniersServisVf1066.includes(it.l));
        const tirage = shuffle(dispo).slice(0, 4);
        const nbFaux = tirage.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        if (new Set(tirage.map((it) => FAMILLE_VF(it.l))).size !== 4) continue;
        derniersServisVf1066 = tirage.map((it) => it.l);
        const payload = makeVraiFaux(
            tirage.map((it) => it.l),
            tirage.map((it) => (it.v ? 0 : 1)),
            '<p>Vues d\'un empilement de cubes. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(L_VF),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        return payload;
    }
    throw new Error('gen_1066 : V/F sans mixité après 60 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    vue_qcm_unique: genVueQcm,
    vue_tableau_vf: genVueVf,
    codee_qcm_unique: genCodeeQcm,
    codee_saisie: genCodeeSaisie,
    vf_tableau_vf: genVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'vue', label: L_VUE, formatsList: buildFormats('vue') },
    { id: 'codee', label: L_CODEE, formatsList: buildFormats('codee') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
